import { Link } from "react-router-dom";
import { MapPin, Sparkles } from "lucide-react";
import { ONE_TIME_PLAN, SUBSCRIPTION_PLANS, billingLine, inr, usePlanAvailability } from "@/lib/plans";

const PlanCard = ({ plan, available }) => {
  const long = plan.months >= 6;
  return (
    <Link
      to={`/plans/${plan.slug}`}
      className={`group relative flex flex-col rounded-3xl p-7 border transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 ${long ? "bg-[#FFFBEB] border-amber-300 shadow-[0_24px_60px_-34px_rgba(217,119,6,0.7)]" : "bg-white border-stone-200 hover:border-amber-300"}`}
      data-testid={`plan-card-${plan.id}`}
    >
      <span className="absolute right-6 top-6 h-2.5 w-2.5 rounded-full bg-amber-300 shadow-[0_0_14px_5px_rgba(250,204,21,0.55)] group-hover:scale-125 transition-transform duration-300" />
      {plan.highlight && <span className="font-hand text-lg text-amber-800 -mt-1 mb-1">{plan.highlight}</span>}
      <p className="text-xs uppercase tracking-[0.3em] text-amber-800 font-semibold">{plan.durationLabel}</p>
      <p className="mt-4 font-serif-display text-5xl text-stone-900" data-testid={`plan-price-${plan.id}`}>{inr(plan.price)}</p>
      <p className="text-sm text-stone-600 mt-2" data-testid={`plan-billing-${plan.id}`}>{billingLine(plan)}</p>
      <p className="text-sm text-stone-600 mt-1">
        {plan.months === 1 ? "One envelope a month" : `${plan.months} envelopes, one each month`}
      </p>
      <div className="mt-auto pt-8 flex items-center justify-between">
        <span className={`text-xs whitespace-nowrap rounded-full px-3 py-1 ${available ? "bg-emerald-50 text-emerald-800" : "bg-stone-100 text-stone-600"}`} data-testid={`plan-availability-${plan.id}`}>
          {available === null ? "…" : available ? "Open now" : "Coming soon"}
        </span>
        <span className="text-sm font-medium whitespace-nowrap text-stone-900 group-hover:text-amber-700 transition-colors" data-testid={`plan-cta-${plan.id}`}>See details →</span>
      </div>
    </Link>
  );
};

const OneTimeCard = ({ plan, available }) => (
  <Link to={`/plans/${plan.slug}`} className="group mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-3xl border border-dashed border-stone-300 bg-white px-7 py-6 hover:border-amber-400 transition-colors duration-300" data-testid={`plan-card-${plan.id}`}>
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-amber-800 font-semibold">Just once</p>
      <p className="font-serif-display text-3xl text-stone-900 mt-1">A single envelope · <span data-testid={`plan-price-${plan.id}`}>{inr(plan.price)}</span></p>
      <p className="text-sm text-stone-600 mt-1" data-testid={`plan-billing-${plan.id}`}>{billingLine(plan)} · perfect as a gift or a first taste</p>
    </div>
    <div className="flex items-center gap-4">
      <span className={`text-xs whitespace-nowrap rounded-full px-3 py-1 ${available ? "bg-emerald-50 text-emerald-800" : "bg-stone-100 text-stone-600"}`} data-testid={`plan-availability-${plan.id}`}>{available === null ? "…" : available ? "Open now" : "Coming soon"}</span>
      <span className="text-sm font-medium whitespace-nowrap text-stone-900 group-hover:text-amber-700 transition-colors" data-testid={`plan-cta-${plan.id}`}>See details →</span>
    </div>
  </Link>
);

export const PlansSection = () => {
  const available = usePlanAvailability();
  const avail = (id) => (available ? !!available[id] : null);
  return (
    <section id="pricing" className="py-28 bg-[#FAF7F2]" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto px-6">
        <p className="font-hand text-3xl text-amber-700 -rotate-1">pull up a chair</p>
        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-stone-900 mt-3">Join The Little Lantern Club</h2>
        <p className="text-stone-600 mt-4 max-w-2xl">Four ways to subscribe, from ₹444 onwards, each priced with a little angel-number magic. Every subscription brings one envelope a month and renews automatically on Razorpay. Or try a single envelope, just once.</p>
        <div className="flex flex-wrap gap-3 mt-6">
          <p className="inline-flex items-center gap-2 text-sm text-stone-700 bg-white border border-stone-200 rounded-full px-4 py-2" data-testid="shipping-note"><MapPin className="h-4 w-4 text-amber-600" /> Shipping within India only · free shipping</p>
          <p className="inline-flex items-center gap-2 text-sm text-stone-700 bg-white border border-stone-200 rounded-full px-4 py-2"><Sparkles className="h-4 w-4 text-amber-600" /> Subscriptions auto-renew</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {SUBSCRIPTION_PLANS.map((p) => <PlanCard key={p.id} plan={p} available={avail(p.id)} />)}
        </div>
        <OneTimeCard plan={ONE_TIME_PLAN} available={avail(ONE_TIME_PLAN.id)} />
      </div>
    </section>
  );
};
